import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { User } from '../types';
import { getCatalystAuth, LOGOUT_REDIRECT_URL, mapCatalystUserToAppUser } from '../lib/catalyst-auth';

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  refreshSession: () => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshSession = async () => {
    setIsLoading(true);

    try {
      const auth = getCatalystAuth();
      const response = await auth.isUserAuthenticated();

      if (response?.content) {
        setUser(mapCatalystUserToAppUser(response.content));
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    getCatalystAuth().signOut(LOGOUT_REDIRECT_URL);
  };

  useEffect(() => {
    refreshSession();
  }, []);

  const value = useMemo(() => ({
    user,
    isAuthenticated: !!user,
    isLoading,
    refreshSession,
    logout
  }), [user, isLoading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }

  return context;
}
